// src/contexts/UserBadgesContext.js
//
// Owns the per-user "badge" Firestore listeners (points total + unread message
// count) once for the whole session. Previously these lived inside TopAppBar's
// PointsButton and BottomNav, which unmount/remount when navigating between
// layouts (MapLayout <-> MainLayout), tearing down and reopening the listeners
// on every such navigation. Mounting them above <Routes> keeps a single stable
// subscription each.

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";
import { isSelfOnlyPrivateCapsule } from "../utils/messageClassification";

function asInt(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

const UserBadgesContext = createContext({ pointsTotal: 0, unreadCount: 0 });
// Raw received + claimed capsule docs, shared so /messages can reuse this
// session-level subscription instead of opening a second identical pair.
const ReceivedCapsulesContext = createContext({ docs: [], loaded: false });

export const useUserBadges = () => useContext(UserBadgesContext);
export const useReceivedCapsuleDocs = () => useContext(ReceivedCapsulesContext);

export const UserBadgesProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || "";
  const [pointsTotal, setPointsTotal] = useState(0);
  const [receivedCapsuleDocs, setReceivedCapsuleDocs] = useState([]);
  const [receivedLoaded, setReceivedLoaded] = useState(false);

  // Points: sum of award_receipts deltaPoints.
  useEffect(() => {
    if (!uid) {
      setPointsTotal(0);
      return undefined;
    }

    const colRef = collection(db, "users", uid, "award_receipts");
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        let sum = 0;
        snap.forEach((d) => {
          sum += asInt((d.data() || {}).deltaPoints, 0);
        });
        setPointsTotal(Math.max(0, sum));
      },
      (err) => {
        console.warn("Failed to read award_receipts:", err);
      }
    );

    return () => unsub();
  }, [uid]);

  // Received + claimed capsule docs (deduped by id). A single subscription
  // drives both the unread badge and the /messages received list.
  useEffect(() => {
    if (!uid) {
      setReceivedCapsuleDocs([]);
      setReceivedLoaded(false);
      return undefined;
    }

    setReceivedLoaded(false);

    const receivedQ = query(
      collection(db, "timeCapsules"),
      where("recipientUserIds", "array-contains", uid)
    );
    const claimedQ = query(
      collection(db, "timeCapsules"),
      where("claimedBy", "array-contains", uid)
    );

    let recipientDocs = [];
    let claimedDocs = [];
    let hasRecipient = false;
    let hasClaim = false;

    const publishMerged = () => {
      const byId = new Map();
      [...recipientDocs, ...claimedDocs].forEach((d) => byId.set(d.id, d));
      setReceivedCapsuleDocs(Array.from(byId.values()));
      if (hasRecipient && hasClaim) setReceivedLoaded(true);
    };

    const unsubscribe = onSnapshot(receivedQ, (snap) => {
      recipientDocs = snap.docs;
      hasRecipient = true;
      publishMerged();
    });

    const unsubscribeClaimed = onSnapshot(claimedQ, (snap) => {
      claimedDocs = snap.docs;
      hasClaim = true;
      publishMerged();
    });

    return () => {
      unsubscribe();
      unsubscribeClaimed();
    };
  }, [uid]);

  const unreadCount = useMemo(() => {
    if (!uid) return 0;
    let count = 0;
    receivedCapsuleDocs.forEach((docSnap) => {
      const data = docSnap.data();
      const readBy = data.readBy || [];
      const archivedBy = data.archivedBy || [];
      const deletedBy = data.deletedBy || [];
      if (
        !isSelfOnlyPrivateCapsule(data, uid) &&
        !readBy.includes(uid) &&
        !archivedBy.includes(uid) &&
        !deletedBy.includes(uid)
      ) {
        count += 1;
      }
    });
    return count;
  }, [receivedCapsuleDocs, uid]);

  const badgesValue = useMemo(
    () => ({ pointsTotal, unreadCount }),
    [pointsTotal, unreadCount]
  );
  const receivedValue = useMemo(
    () => ({ docs: receivedCapsuleDocs, loaded: receivedLoaded }),
    [receivedCapsuleDocs, receivedLoaded]
  );

  return (
    <UserBadgesContext.Provider value={badgesValue}>
      <ReceivedCapsulesContext.Provider value={receivedValue}>
        {children}
      </ReceivedCapsulesContext.Provider>
    </UserBadgesContext.Provider>
  );
};

export default UserBadgesContext;
